import {
    Box,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    makeStyles,
} from "@material-ui/core";
import {
    AccountBalance,
    People,
    Refresh,
    TrendingDown,
    TrendingUp,
} from "@material-ui/icons";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PaymentService } from "../../services/PaymentService";
import { SimpleDashboardService } from "../../services/SimpleDashboardService";
import UserContext from "../UserContext";
import ComparisonBar from "./ComparisonBar";
import Donut from "./Donut";
import RecentTransactions, {
    RecentTransaction,
} from "./RecentTransactions";
import SimpleDashboardCard from "./SimpleDashboardCard";
import TopOutstandingClients, {
    OutstandingClient,
} from "./TopOutstandingClients";

const RECENT_TRANSACTION_LIMIT = 10;
const TOP_OUTSTANDING_LIMIT = 4;
const OUTSTANDING_FETCH_LIMIT = 200;

const fetchRecentTransactions = async (): Promise<RecentTransaction[]> => {
    const [meta] = await PaymentService.getDetailCreditPaymentsAgainstBank(
        1,
        1,
        {}
    );
    const total = parseAmount(meta?.count);
    if (!total) return [];

    const page = Math.max(1, Math.ceil(total / RECENT_TRANSACTION_LIMIT));
    const [data] = await PaymentService.getDetailCreditPaymentsAgainstBank(
        page,
        RECENT_TRANSACTION_LIMIT,
        {}
    );

    return [...(data?.rows || [])].reverse();
};

const fetchTopOutstandingClients = async (): Promise<OutstandingClient[]> => {
    const [data] = await PaymentService.getPaymentsToBePaid(
        1,
        OUTSTANDING_FETCH_LIMIT,
        {}
    );
    const rows: OutstandingClient[] = data?.rows || [];

    return [...rows]
        .sort(
            (a, b) =>
                Math.abs(Number(b.balance) || 0) -
                Math.abs(Number(a.balance) || 0)
        )
        .slice(0, TOP_OUTSTANDING_LIMIT);
};

type TotalsData = {
    totalCredit: number;
    totalDebit: number;
    TotalAdjustment: number;
    TotalAdvance: number;
    TotalRefund: number;
    TotalBalance: number;
};

type TodayData = {
    TodayCredit: number;
    TodayDebit: number;
    TodayAdjustment: number;
    TodayAdvance: number;
    TodayRefund: number;
    TodayBalance: number;
};

const emptyTotals: TotalsData = {
    totalCredit: 0,
    totalDebit: 0,
    TotalAdjustment: 0,
    TotalAdvance: 0,
    TotalRefund: 0,
    TotalBalance: 0,
};

const emptyToday: TodayData = {
    TodayCredit: 0,
    TodayDebit: 0,
    TodayAdjustment: 0,
    TodayAdvance: 0,
    TodayRefund: 0,
    TodayBalance: 0,
};

const parseAmount = (value: unknown) => Number(value) || 0;

const normalizeTotals = (data: any): TotalsData => ({
    totalCredit: parseAmount(data?.totalCredit),
    totalDebit: parseAmount(data?.totalDebit),
    TotalAdjustment: parseAmount(data?.TotalAdjustment),
    TotalAdvance: parseAmount(data?.TotalAdvance),
    TotalRefund: parseAmount(data?.TotalRefund),
    TotalBalance: parseAmount(data?.TotalBalance),
});

const normalizeToday = (data: any): TodayData => ({
    TodayCredit: parseAmount(data?.TodayCredit),
    TodayDebit: parseAmount(data?.TodayDebit),
    TodayAdjustment: parseAmount(data?.TodayAdjustment),
    TodayAdvance: parseAmount(data?.TodayAdvance),
    TodayRefund: parseAmount(data?.TodayRefund),
    TodayBalance: parseAmount(data?.TodayBalance),
});

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: 1400,
        margin: "0 auto",
    },
    header: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: theme.spacing(3),
        gap: theme.spacing(2),
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
        color: theme.palette.text.primary,
    },
    sectionPaper: {
        padding: theme.spacing(2),
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
    },
    insightText: {
        color: theme.palette.text.secondary,
        fontSize: "0.9rem",
        lineHeight: 1.6,
    },
    loadingWrap: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 320,
    },
}));

const SimpleDashboard = () => {
    const classes = useStyles();
    const { settings, user } = useContext(UserContext);
    const [totalAmount, setTotalAmount] = useState<TotalsData>(emptyTotals);
    const [singleDayAmount, setSingleDayAmount] =
        useState<TodayData>(emptyToday);
    const [outstandingClients, setOutstandingClients] = useState(0);
    const [recentTransactions, setRecentTransactions] = useState<
        RecentTransaction[]
    >([]);
    const [topOutstandingClients, setTopOutstandingClients] = useState<
        OutstandingClient[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const orgName =
        settings?.["organization-name"]?.trim() ||
        (user?.baseUser?.type === "user" && user?.classroom?.class
            ? user.classroom.class
            : "Dashboard");

    const todayLabel = useMemo(
        () =>
            new Intl.DateTimeFormat("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
            }).format(new Date()),
        []
    );

    const loadDashboard = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const [[totalData], [todayData], [outstandingData], recentRows, topClients] =
            await Promise.all([
                SimpleDashboardService.getTotalAmount({}),
                SimpleDashboardService.getSingleDayAmount({}),
                PaymentService.getPaymentsToBePaid(1, 1, {}),
                fetchRecentTransactions(),
                fetchTopOutstandingClients(),
            ]);

        if (totalData) setTotalAmount(normalizeTotals(totalData));
        if (todayData) setSingleDayAmount(normalizeToday(todayData));
        if (outstandingData) {
            setOutstandingClients(parseAmount(outstandingData?.count));
        }
        setRecentTransactions(recentRows);
        setTopOutstandingClients(topClients);

        setLoading(false);
        setRefreshing(false);
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const todayNet = singleDayAmount.TodayDebit - singleDayAmount.TodayCredit;

    if (loading) {
        return (
            <Box className={classes.loadingWrap}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box>
                    <Typography variant="h4" style={{ fontWeight: 700 }}>
                        {orgName}
                    </Typography>
                    <Typography className={classes.insightText}>
                        Financial overview · {todayLabel}
                    </Typography>
                </Box>
                <Tooltip title="Refresh dashboard">
                    <IconButton
                        onClick={() => loadDashboard(true)}
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <CircularProgress size={22} />
                        ) : (
                            <Refresh />
                        )}
                    </IconButton>
                </Tooltip>
            </Box>

            <Grid container spacing={2} className={classes.section}>
                <Grid item xs={12} sm={6} lg={3}>
                    <SimpleDashboardCard
                        label="Outstanding Balance"
                        value={totalAmount.TotalBalance}
                        subtitle="Total receivable from clients"
                        accent="#424242"
                        icon={<AccountBalance fontSize="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <SimpleDashboardCard
                        label="Total Collections"
                        value={totalAmount.totalDebit}
                        subtitle="All-time payments received"
                        accent="#2e7d32"
                        icon={<TrendingDown fontSize="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <SimpleDashboardCard
                        label="Total Charges"
                        value={totalAmount.totalCredit}
                        subtitle="All-time sales & credits"
                        accent="#e65100"
                        icon={<TrendingUp fontSize="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <SimpleDashboardCard
                        label="Clients With Balance"
                        value={outstandingClients}
                        subtitle="Clients owing payment"
                        accent="#fcca4a"
                        icon={<People fontSize="small" />}
                        format="number"
                    />
                </Grid>
            </Grid>

            <Paper className={classes.sectionPaper} elevation={0}>
                <Typography className={classes.sectionTitle}>
                    Today at a glance
                </Typography>
                <Typography className={classes.insightText}>
                    Collected {parseAmount(singleDayAmount.TodayDebit).toLocaleString()} PKR
                    today
                    {todayNet !== 0 && (
                        <>
                            {" "}
                            · Net flow{" "}
                            <strong>
                                {todayNet >= 0 ? "+" : ""}
                                {todayNet.toLocaleString()} PKR
                            </strong>{" "}
                            ({todayNet >= 0 ? "more collected than charged" : "more charged than collected"})
                        </>
                    )}
                </Typography>
            </Paper>

            <Grid container spacing={2} className={classes.section}>
                <Grid item xs={12} lg={6}>
                    <RecentTransactions transactions={recentTransactions} />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <TopOutstandingClients clients={topOutstandingClients} />
                </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.section}>
                <Grid item xs={12} lg={6}>
                    <ComparisonBar
                        title="All-Time Flow"
                        credit={totalAmount.totalCredit}
                        debit={totalAmount.totalDebit}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <ComparisonBar
                        title="Today's Flow"
                        credit={singleDayAmount.TodayCredit}
                        debit={singleDayAmount.TodayDebit}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} className={classes.section}>
                <Grid item xs={12} lg={6}>
                    <Donut
                        title="All-Time Breakdown"
                        subtitle="Distribution across payment categories"
                        series={[
                            totalAmount.totalCredit,
                            totalAmount.totalDebit,
                            totalAmount.TotalAdvance,
                            totalAmount.TotalAdjustment,
                            totalAmount.TotalRefund,
                            Math.abs(totalAmount.TotalBalance),
                        ]}
                        labels={[
                            "Credit (Charges)",
                            "Debit (Collections)",
                            "Advance",
                            "Adjustments",
                            "Refunds",
                            "Outstanding",
                        ]}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <Donut
                        title="Today's Breakdown"
                        subtitle="Activity recorded today only"
                        series={[
                            singleDayAmount.TodayCredit,
                            singleDayAmount.TodayDebit,
                            singleDayAmount.TodayAdvance,
                            singleDayAmount.TodayAdjustment,
                            singleDayAmount.TodayRefund,
                            Math.abs(singleDayAmount.TodayBalance),
                        ]}
                        labels={[
                            "Credit (Charges)",
                            "Debit (Collections)",
                            "Advance",
                            "Adjustments",
                            "Refunds",
                            "Net Balance",
                        ]}
                    />
                </Grid>
            </Grid>

            <Typography className={classes.sectionTitle}>
                Detailed figures
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Paper className={classes.sectionPaper} elevation={0}>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 700, marginBottom: 12 }}
                        >
                            All time
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Adjustments"
                                    value={totalAmount.TotalAdjustment}
                                    accent="#2196f3"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Advances"
                                    value={totalAmount.TotalAdvance}
                                    accent="#2196f3"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Refunds"
                                    value={totalAmount.TotalRefund}
                                    accent="#757575"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Net Balance"
                                    value={totalAmount.TotalBalance}
                                    accent="#424242"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper className={classes.sectionPaper} elevation={0}>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 700, marginBottom: 12 }}
                        >
                            Today
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Today's Credit"
                                    value={singleDayAmount.TodayCredit}
                                    accent="#e65100"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Today's Debit"
                                    value={singleDayAmount.TodayDebit}
                                    accent="#2e7d32"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Today's Adjustments"
                                    value={singleDayAmount.TodayAdjustment}
                                    accent="#2196f3"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Today's Advances"
                                    value={singleDayAmount.TodayAdvance}
                                    accent="#2196f3"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Today's Refunds"
                                    value={singleDayAmount.TodayRefund}
                                    accent="#757575"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SimpleDashboardCard
                                    label="Today's Net"
                                    value={singleDayAmount.TodayBalance}
                                    accent="#fcca4a"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SimpleDashboard;
